import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Sparkles, Copy, Check, Save } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useSalaryProfile } from "@/hooks/useSalaryProfile";
import { useSaveNegotiationScenario } from "@/hooks/useSaveNegotiationScenario";

export function NegotiationStrategy() {
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const { data: profileData } = useSalaryProfile();
  const saveScenario = useSaveNegotiationScenario();

  const [formData, setFormData] = useState({
    jobTitle: "",
    yearsExperience: "",
    currentSalary: "",
    targetSalary: "",
    scenario: "initial_offer",
  });

  // Auto-fill from profile
  useEffect(() => {
    if (profileData && !formData.jobTitle) {
      setFormData(prev => ({
        ...prev,
        jobTitle: profileData.jobTitle,
        yearsExperience: profileData.yearsExperience.toString(),
      }));
    }
  }, [profileData]);

  const handleGenerate = async () => {
    if (!formData.jobTitle || !formData.targetSalary) {
      toast({
        title: "Missing Information",
        description: "Please provide job title and target salary",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setStrategy("");

    try {
      // Generate context hash for idempotency
      const encoder = new TextEncoder();
      const contextData = `${formData.jobTitle}:${formData.targetSalary}:${formData.scenario}`;
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(contextData));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const contextHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `salary:negotiation:${contextHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'salary.negotiation_strategy', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast({
            title: "Insufficient Credits",
            description: errorData.message || 'Not enough credits',
            variant: "destructive",
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Generate strategy
      const { data, error } = await supabase.functions.invoke('generate-negotiation-strategy', {
        body: {
          jobTitle: formData.jobTitle,
          yearsExperience: parseInt(formData.yearsExperience) || 0,
          currentSalary: parseFloat(formData.currentSalary) || null,
          targetSalary: parseFloat(formData.targetSalary),
          scenario: formData.scenario,
        },
      });

      if (error) throw error;

      setStrategy(data.strategy);
      
      // Auto-save scenario
      saveScenario.mutate({
        offerAmount: parseFloat(formData.currentSalary) || 0,
        counterOffer: parseFloat(formData.targetSalary),
        negotiationStrategy: data.strategy,
      });

      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast({
          title: "Strategy Generated",
          description: `Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`,
        });
      } else {
        toast({
          title: "Strategy Generated",
          description: "Your personalized negotiation strategy is ready",
        });
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error('[SALARY-NEGO-STRATEGY] Error:', error);
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate strategy",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(strategy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({
      title: "Copied!",
      description: "Strategy copied to clipboard",
    });
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertDescription>
          Get AI-powered negotiation scripts, tactics, and email templates tailored to your situation.
        </AlertDescription>
      </Alert>

      <Card className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold">Your Situation</h3>
          {profileData && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">Auto-filled from profile</span>
          )}
        </div>
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                placeholder="Senior Software Engineer"
                value={formData.jobTitle}
                onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="yearsExperience">Years of Experience</Label>
              <Input
                id="yearsExperience"
                type="number"
                placeholder="5"
                value={formData.yearsExperience}
                onChange={(e) => setFormData({ ...formData, yearsExperience: e.target.value })}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="currentSalary">Current Salary (optional)</Label>
              <Input
                id="currentSalary"
                type="number"
                placeholder="90000"
                value={formData.currentSalary}
                onChange={(e) => setFormData({ ...formData, currentSalary: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="targetSalary">Target Salary</Label>
              <Input
                id="targetSalary"
                type="number"
                placeholder="120000"
                value={formData.targetSalary}
                onChange={(e) => setFormData({ ...formData, targetSalary: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="scenario">Negotiation Scenario</Label>
            <Select value={formData.scenario} onValueChange={(value) => setFormData({ ...formData, scenario: value })}>
              <SelectTrigger id="scenario">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="initial_offer">Responding to Initial Offer</SelectItem>
                <SelectItem value="counter_offer">Making a Counter-Offer</SelectItem>
                <SelectItem value="raise_request">Requesting a Raise</SelectItem>
                <SelectItem value="general">General Negotiation</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleGenerate} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Strategy...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Negotiation Strategy
              </>
            )}
          </Button>
        </div>
      </Card>

      {strategy && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Your Personalized Strategy</h3>
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <Textarea
            value={strategy}
            readOnly
            className="min-h-[400px] font-mono text-sm"
          />
        </Card>
      )}
    </div>
  );
}
